import React, { useState, useEffect } from 'react';
import { Mail, ArrowRight, Zap, User, Share, PlusSquare, Shield, Radio, Eye, EyeOff } from 'lucide-react';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { signInWithEmailAndPassword } from 'firebase/auth';
import TermsModal, { TERMS_VERSION } from './TermsModal';

const EmailGate = ({ onComplete, db, auth }) => {
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(false);

  // Guideline 1.2: users must agree to the terms BEFORE registering. The
  // checkbox gates every path through this form (regular + staff login).
  const [agreed, setAgreed] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  
  // PWA State
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  // The admin backdoor is WEB-ONLY: excluded from the native App Store build to
  // avoid App Review guideline 2.3.1 (hidden/undocumented features). Admins use
  // the web app (bendermap-69.web.app) for venue management instead.
  const isNativeApp = typeof window !== 'undefined' && !!window.Capacitor?.isNativePlatform?.();

  useEffect(() => {
    // 1. Detect iOS
    const isIosDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(isIosDevice);

    // 2. Detect "Standalone" (Installed) Mode
    const isStandaloneMode = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone || window.Capacitor?.isNativePlatform?.();
    setIsStandalone(isStandaloneMode);
  }, []);

  // Staff password is an INLINE field (shown when the username is BENDER_ADMIN),
  // not a window.prompt dialog — one screen, one button, nothing to miss.
  const [password, setPassword] = useState('');
  // Every validation failure is rendered visibly under the form. This form
  // also runs with noValidate: iOS WebKit blocks a submit on `required` /
  // type="email" constraint failures WITHOUT any visible feedback, which App
  // Review reported three times as "the login button was not responsive"
  // (their password landed in the email field). Native validation must never
  // be allowed to swallow a tap silently.
  const [error, setError] = useState('');
  const isStaff = username.trim() === 'BENDER_ADMIN';
  // Staff mode hides the email box (defaults to the primary staff account);
  // this reveals it for the other staff login.
  const [showStaffEmail, setShowStaffEmail] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    // Dismiss the keyboard NOW — unmounting this gate while WKWebView is still
    // resizing for the keyboard triggered the Dashboard's unpainted-layer glitch.
    try { document.activeElement?.blur(); } catch { /* ignore */ }

    if (!username.trim()) {
        setError('Enter a username.');
        return;
    }

    // --- 0. TERMS GATE (guideline 1.2) ---
    if (!agreed) {
        setError('Please agree to the Terms of Use to continue.');
        setShowTerms(true); // show the full terms; agreeing there checks the box
        return;
    }

    // --- 1. STAFF LOGIN (single credential, real Firebase auth) ---
    // One login for cody@/cameo@: signs in with a real email/password account so
    // the token carries the email that Firestore rules (isAdmin) require. Routes
    // by platform — web goes to the admin dashboard (venue management), native
    // drops into rep broadcast mode in the field. The password IS the gate; the
    // email only picks which staff account — anything unrecognised (blank,
    // invalid, or the reviewer's password typed there) resolves to the primary.
    if (isStaff) {
        const staffEmails = ['cody@benderextender.com', 'cameo@benderextender.com'];
        let staffEmail = email.toLowerCase().trim();
        if (!staffEmails.includes(staffEmail)) staffEmail = 'cody@benderextender.com';
        if (!password) {
            setError('Enter the staff password.');
            return;
        }
        setLoading(true);
        try {
            await signInWithEmailAndPassword(auth, staffEmail, password);
            if (isNativeApp) {
                // Field rep mode: drop into the normal map app with the broadcast panel.
                const repName = staffEmail.split('@')[0];
                localStorage.setItem('bender_email', staffEmail);
                localStorage.setItem('bender_username', repName);
                onComplete(staffEmail, repName, false, true); // 4th arg = isRep
            } else {
                // Web: admin dashboard (venue management).
                onComplete(staffEmail, username, true);
            }
        } catch (err) {
            console.warn('Staff sign-in failed:', err && err.code);
            setError('Access denied — wrong staff password.');
            setLoading(false);
        }
        return;
    }

    // --- 2. VALIDATION (visible, ours — never the browser's silent kind) ---
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
    if (!emailRegex.test(email.trim())) {
        setError('Enter a valid email address (e.g. you@example.com).');
        return;
    }

    setLoading(true);

    // Create a promise that rejects after 3 seconds (Safety Valve)
    const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error("Request timed out")), 3000)
    );

    try {
      // Race the database write against the 3-second timer
      await Promise.race([
          setDoc(doc(db, "marketing_leads", email), {
            email: email,
            username: username,
            capturedAt: serverTimestamp(),
            source: "mybender_beta",
            termsAccepted: TERMS_VERSION
          }),
          timeoutPromise
      ]);

      // Success path (Normal User)
      localStorage.setItem('bender_email', email);
      localStorage.setItem('bender_username', username);
      onComplete(email, username, false); // false = not an admin
    } catch (err) {
      console.warn("Lead capture skipped/failed (offline or timeout):", err);

      // FALLBACK: Let them in anyway
      localStorage.setItem('bender_email', email);
      localStorage.setItem('bender_username', username);
      onComplete(email, username, false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9000] bg-black flex flex-col items-center justify-center p-6 font-sans overflow-y-auto">
      
      {/* BRANDING HEADER */}
      <div className="w-full max-w-sm flex flex-col items-center mb-8 animate-in slide-in-from-top-4 duration-700">
        <div className="relative mb-6">
            <div className="absolute inset-0 rounded-full"></div>
            <img 
                src="/bender-logo.png" 
                alt="Bender Extender" 
                className="w-40 h-40 object-contain relative z-10 opacity-90" 
            />
        </div>
        
        <h1 className="text-4xl font-black italic text-white tracking-tighter leading-none mb-2 drop-shadow-xl">
            BENDER <span className="text-brand-pink">QUEST</span>
        </h1>
        <p className="text-zinc-500 text-xs font-bold uppercase tracking-[0.2em]">
            Track the night. Survive the morning.
        </p>
      </div>

      {/* PWA INSTALL PROMPT */}
      {isIOS && !isStandalone && (
        <div className="w-full max-w-sm mb-6 bg-indigo-900/30 border border-indigo-500/30 p-4 rounded-2xl animate-in fade-in zoom-in-95 duration-500 backdrop-blur-sm">
            <div className="flex items-center gap-2 mb-2">
                <PlusSquare className="text-indigo-400" size={16} />
                <p className="text-xs font-black text-white uppercase italic tracking-wide">Install App Mode</p>
            </div>
            <p className="text-[10px] text-indigo-200 font-bold leading-relaxed">
                Tap the <Share size={12} className="inline mx-0.5 text-white" /> icon below and select <span className="text-white bg-white/10 px-1 rounded">Add to Home Screen</span> to enable full-screen GPS.
            </p>
        </div>
      )}

      {/* FORM CARD */}
      <div className="w-full max-w-sm animate-in fade-in zoom-in-95 duration-500 delay-100">
        <form onSubmit={handleSubmit} noValidate className="bg-zinc-900 border border-white/10 p-8 rounded-[2.5rem] shadow-2xl relative overflow-hidden">
          
          <div className="absolute top-0 right-0 w-40 h-40 bg-brand-teal/10 rounded-full blur-3xl -mr-20 -mt-20 pointer-events-none"></div>

          <label className="text-[10px] font-black text-brand-teal uppercase mb-4 flex items-center gap-2 tracking-widest">
            <Zap size={14} /> Unlock Your Session
          </label>
          
          {/* USERNAME INPUT */}
          <div className="relative mb-3">
            <User className="absolute left-4 top-4 text-zinc-500" size={20} />
            <input 
              type="text"
              placeholder="Username / Handle"
              value={username}
              onChange={(e) => { setUsername(e.target.value); setError(''); }}
              className="w-full bg-black/50 border border-zinc-700 rounded-2xl py-4 pl-12 pr-4 text-white font-bold focus:outline-none focus:border-brand-pink transition-colors placeholder:text-zinc-700 text-sm"
            />
          </div>

          {/* SECOND FIELD — email for everyone, but the moment the username is
              BENDER_ADMIN this box BECOMES the password field, so the screen shows
              exactly the two things App Store Connect gives a reviewer (user name,
              password) in that order. The staff email defaults to the primary
              account; a small link reveals the email box for the other staff login. */}
          {!isStaff || showStaffEmail ? (
            <div className="relative mb-4">
              <Mail className="absolute left-4 top-4 text-zinc-500" size={20} />
              <input
                type="email"
                placeholder={isStaff ? 'staff@benderextender.com' : 'enter@your.email'}
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(''); }}
                className="w-full bg-black/50 border border-zinc-700 rounded-2xl py-4 pl-12 pr-4 text-white font-bold focus:outline-none focus:border-brand-pink transition-colors placeholder:text-zinc-700 text-sm"
              />
            </div>
          ) : null}

          {isStaff && (
            <div className="mb-4 animate-in fade-in slide-in-from-top-1">
              <div className="relative">
                <Shield className="absolute left-4 top-4 text-zinc-500" size={20} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setError(''); }}
                  className="w-full bg-black/50 border border-indigo-500/50 rounded-2xl py-4 pl-12 pr-12 text-white font-bold focus:outline-none focus:border-indigo-400 transition-colors placeholder:text-zinc-700 text-sm"
                />
                {/* Reveal toggle — lets a reviewer confirm what they typed. */}
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  className="absolute right-4 top-4 text-zinc-500 hover:text-white"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {!showStaffEmail && (
                <button
                  type="button"
                  onClick={() => setShowStaffEmail(true)}
                  className="mt-2 text-[10px] font-bold text-zinc-500 underline"
                >
                  Signing in as cody@benderextender.com — use a different staff email
                </button>
              )}
            </div>
          )}

          {/* VISIBLE VALIDATION — never rely on the browser's silent iOS behaviour */}
          {error && (
            <p role="alert" className="mb-4 text-[11px] font-bold text-red-400 bg-red-950/40 border border-red-900 rounded-xl px-3 py-2">
              {error}
            </p>
          )}

          {/* TERMS AGREEMENT — required before any login path (guideline 1.2) */}
          <label className="flex items-start gap-3 mb-5 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="mt-0.5 w-4 h-4 shrink-0 accent-[var(--accent-1)]"
            />
            <span className="text-[10px] font-bold text-zinc-400 leading-snug">
              I agree to the{' '}
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); setShowTerms(true); }}
                className="text-brand-pink underline font-black"
              >
                Terms of Use
              </button>
              {' '}&amp;{' '}
              <a
                href="https://bendermap-69.web.app/privacy.html"
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand-pink underline font-black"
              >
                Privacy Policy
              </a>
              . Zero tolerance for objectionable content or abusive behavior.
            </span>
          </label>

          <button
            type="submit"
            disabled={loading}
            className={`w-full text-white font-black py-5 rounded-2xl flex items-center justify-center gap-3 uppercase tracking-widest transition-transform active:scale-95 disabled:opacity-70 shadow-[0_0_30px_rgba(230,131,169,0.3)] text-sm ${
                username === 'BENDER_ADMIN' ? (isNativeApp ? 'bg-brand-pink animate-pulse' : 'bg-indigo-600 animate-pulse')
                    : 'bg-brand-teal'
            }`}
          >
            {loading ? (
                <span className="animate-pulse">Processing...</span>
            ) : username === 'BENDER_ADMIN' ? (
                isNativeApp
                    ? <> <Radio size={18} /> REP LOGIN</>
                    : <> <Shield size={18} /> ADMIN LOGIN</>
            ) : (
                <>
                  {isStandalone ? "ENTER MAP" : "START NIGHT"} 
                  <ArrowRight size={18} />
                </>
            )}
          </button>
        </form>
      </div>

      {/* Full terms — scrollable, with Agree checking the box */}
      {showTerms && (
        <TermsModal
          onAgree={() => { setAgreed(true); setShowTerms(false); }}
          onClose={() => setShowTerms(false)}
        />
      )}
    </div>
  );
};

export default EmailGate;
